import React from 'react';
import { Edit, Eye, Play, Plus, RefreshCw, Users } from 'lucide-react';
import { Client } from '../../types';
import { ClientHealthBadge } from './ClientHealthBadge';
import { IntegrationStatusBadges } from './IntegrationStatusBadges';
import { usePermissions } from '../../hooks/usePermissions';

interface ClientTableProps {
	clients: Client[];
	loading: boolean;
	onEdit: (client: Client) => void;
	onView: (client: Client) => void;
	onAdd: () => void;
}

export const ClientTable: React.FC<ClientTableProps> = ({ clients, loading, onEdit, onView, onAdd }) => {
	const { canUpdateClient, canCreateClient } = usePermissions();
	if (loading) {
		return (
			<div className="flex items-center justify-center py-12">
				<RefreshCw className="animate-spin h-6 w-6 text-blue-600" />
			</div>
		);
	}

	if (clients.length === 0) {
		return (
			<div className="text-center py-12">
				<Users className="mx-auto h-12 w-12 text-gray-400" />
				<h3 className="mt-2 text-sm font-medium text-gray-900">No clients found</h3>
				<p className="mt-1 text-sm text-gray-500">
					{canCreateClient ? 'Get started by adding your first client.' : 'No client data available.'}
				</p>
				{canCreateClient && (
					<button
						onClick={onAdd}
						className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center mx-auto">
						<Plus className="h-4 w-4 mr-2" />
						Add Your First Client
					</button>
				)}
			</div>
		);
	}

	return (
		<div className="overflow-x-auto">
			<table className="min-w-full">
				<thead className="bg-gray-50">
					<tr>
						<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLIENT</th>
						<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">STATUS</th>
						<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							INTEGRATIONS
						</th>
						<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HEALTH</th>
						<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							LAST 7 DAYS
						</th>
						<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							SUCCESS RATE
						</th>
						<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ACTIONS</th>
					</tr>
				</thead>
				<tbody className="bg-white divide-y divide-gray-200">
					{clients.map((client) => (
						<tr key={client.id} className="hover:bg-gray-50">
							<td className="px-6 py-4 whitespace-nowrap">
								<div>
									<div className="text-sm font-medium text-gray-900">{client.name}</div>
									<div className="text-sm text-gray-500">{client.email}</div>
								</div>
							</td>
							<td className="px-6 py-4 whitespace-nowrap">
								<span
									className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
										client.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
									}`}>
									{client.status}
								</span>
							</td>
							<td className="px-6 py-4 whitespace-nowrap">
								<IntegrationStatusBadges integrations={client.integrations} clientId={client.id} />
							</td>
							<td className="px-6 py-4 whitespace-nowrap">
								<ClientHealthBadge health={client.health} />
							</td>
							<td className="px-6 py-4 whitespace-nowrap">
								<div className="text-sm text-gray-900">{client.monthlyVolume.toLocaleString()}</div>
								<div className="text-xs text-gray-500">syncs last 7 days</div>
							</td>
							<td className="px-6 py-4 whitespace-nowrap">
								<div className="text-sm text-gray-900">{client.syncStats.successRate.toFixed(2)}%</div>
								{client.syncStats.failedSyncs > 0 && (
									<div className="text-xs text-red-500">{client.syncStats.failedSyncs} failed</div>
								)}
							</td>
							<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
								<div className="flex items-center space-x-2">
									<button disabled={true} className="flex items-center text-gray-400 cursor-not-allowed">
										<Play className="h-4 w-4 mr-1" />
										Sync
									</button>
									<button
										onClick={() => onView(client)}
										className="text-gray-600 hover:text-gray-900 flex items-center">
										<Eye className="h-4 w-4 mr-1" />
										View
									</button>
									{canUpdateClient && (
										<button
											onClick={() => onEdit(client)}
											className="text-gray-600 hover:text-gray-900 flex items-center">
											<Edit className="h-4 w-4 mr-1" />
											Edit
										</button>
									)}
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};
